import Button from 'components/base/Button';
import { useState } from 'react';
import { Col, Form, Row, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';

const SignUpForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');

  const auth = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    if (!name.trim()) {
      setErrorMessage('Please enter your company name');
      return false;
    }
    if (!email.trim()) {
      setErrorMessage('Please enter your workplace email address');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return false;
    }
    if (!termsAccepted) {
      setErrorMessage('Please accept the terms and privacy policy');
      return false;
    }
    return true;
  };

  const handleSignUp = async () => {
    setErrorMessage('');
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await auth.handleSignUp(email, password, name);
      if (result.success) {
        if (result.message === 'CONFIRM_EMAIL') {
          setSuccessMessage('Account created! Please check your email for a confirmation code.');
          setShowConfirmation(true);
        } else {
          setSuccessMessage(result.message);
          setTimeout(() => {
            navigate(`/pages/authentication/${layout}/sign-in`);
          }, 2000);
        }
      } else {
        setErrorMessage(result.message);
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignUp = async () => {
    setErrorMessage('');
    setSuccessMessage('');

    if (!confirmationCode.trim()) {
      setErrorMessage('Please enter the confirmation code');
      return;
    }

    setIsLoading(true);

    try {
      const result = await auth.handleConfirmSignUp(email, confirmationCode);
      if (result.success) {
        setSuccessMessage(result.message);
        setTimeout(() => {
          navigate(`/pages/authentication/${layout}/sign-in`);
        }, 2000);
      } else {
        setErrorMessage(result.message);
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (showConfirmation) {
    return (
      <>
        <div className="text-center mb-7">
          <h3 className="text-1000">Confirm Your Email</h3>
          <p className="text-700">Enter the confirmation code sent to {email}</p>
        </div>

        {successMessage && (
          <Alert variant="success" className="mb-3">
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert variant="danger" className="mb-3">
            {errorMessage}
          </Alert>
        )}

        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="confirmationCode">Confirmation Code</Form.Label>
          <Form.Control
            id="confirmationCode"
            type="text"
            placeholder="Enter 6-digit code"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
          />
        </Form.Group>

        <Button
          variant="primary"
          className="w-100 mb-3"
          onClick={handleConfirmSignUp}
          disabled={isLoading}
        >
          {isLoading ? 'Confirming...' : 'Confirm Email'}
        </Button>

        <div className="text-center">
          <Link
            to={`/pages/authentication/${layout}/sign-in`}
            className="fs-9 fw-bold"
          >
            Back to Sign In
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="text-center mb-7">
        <h3 className="text-1000">Let&apos;s Start Building</h3>
        <p className="text-700">Create your carrier data foundation</p>
      </div>

      {successMessage && (
        <Alert variant="success" className="mb-3">
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">
          {errorMessage}
        </Alert>
      )}

      <Form>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="name">Company Name</Form.Label>
          <Form.Control
            id="name"
            type="text"
            placeholder="Company Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="email">Workplace email address</Form.Label>
          <Form.Control
            id="email"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Form.Group>
        <Row className="g-3 mb-3">
          <Col sm={layout === 'card' ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="password">Password</Form.Label>
              <Form.Control
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col sm={layout === 'card' ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="confirmPassword">
                Confirm Password
              </Form.Label>
              <Form.Control
                id="confirmPassword"
                type="password"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Check type="checkbox" className="mb-3">
          <Form.Check.Input
            type="checkbox"
            name="termsService"
            id="termsService"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
          />
          <Form.Check.Label htmlFor="termsService" className="fs-9 text-none">
            I accept the{' '}
            <a
              href="https://app.shiplab.com/legal/service-agreement/"
              target="_blank"
              rel="noreferrer"
            >
              terms
            </a>{' '}
            and{' '}
            <a
              href="https://www.shiplab.com/privacy-policy/"
              target="_blank"
              rel="noreferrer"
            >
              privacy policy
            </a>
          </Form.Check.Label>
        </Form.Check>
        <Button
          variant="primary"
          className="w-100 mb-3"
          onClick={handleSignUp}
          disabled={isLoading}
        >
          {isLoading ? 'Signing up...' : 'Sign up'}
        </Button>
        <div className="text-center">
          <Link
            to={`/pages/authentication/${layout}/sign-in`}
            className="fs-9 fw-bold"
          >
            Sign in to an existing account
          </Link>
        </div>
      </Form>
    </>
  );
};

export default SignUpForm;
